import React, { useState, useMemo } from "react";
import { useToast } from "./Toast";
import { useConfirm } from "./ConfirmDialog";
import { getResetWeekForDate } from "./lib/time";

// Type declaration for custom window property
declare global {
  interface Window {
    refreshPlannerRaids?: () => void;
  }
}

const CLASS_COLORS: Record<string, string> = {
  "Warrior": "#C79C6E", "Paladin": "#F58CBA", "Hunter": "#ABD473", "Rogue": "#FFF569",
  "Priest": "#FFFFFF", "Death Knight": "#C41F3B", "Shaman": "#0070DE", "Mage": "#40C7EB",
  "Warlock": "#8787ED", "Druid": "#FF7D0A", "Demon Hunter": "#A330C9", "Unknown": "#525252"
};

export default function MilpResultsPage() {
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const [targetDate, setTargetDate] = useState(new Date().toISOString().slice(0, 10));
  const [isSolving, setIsSolving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  
  // NEW: State for the modal
  const [selectedRaid, setSelectedRaid] = useState<any | null>(null);

  // Calculate Lockout Week Range for UI clarity
  const lockoutRange = useMemo(() => {
    // MILP is admin-only and still anchors its grid on Wednesday, so this uses
    // the shared helper's EU default to stay in step with the solver.
    const wed = new Date(getResetWeekForDate(new Date(targetDate)) + 'T00:00:00Z');
    const tue = new Date(wed); tue.setUTCDate(wed.getUTCDate() + 6);
    return {
        start: wed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        end: tue.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    };
  }, [targetDate]);

  const handleGenerate = async () => {
    setIsSolving(true); setResult(null);
    try {
      const res = await fetch("/milp/solve", {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ date: targetDate })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Solver failed");
      setResult(data);
      if (data.stats) showToast(`Generated ${data.stats.total_raids} raids`, "success");
    } catch(e: any) { showToast(e.message, "error"); } finally { setIsSolving(false); }
  };

  const handlePublish = async () => {
    if (!result?.raids) return;
    
    const confirmed = await confirm({ 
        title: "Publish Schedule?", 
        description: `Confirm ${result.raids.length} raids?`, 
        confirmText: "Publish", 
        variant: "info" 
    });
    
    if (!confirmed) return;
    
    setIsPublishing(true);
    try {
      const res = await fetch("/milp/apply", {
            method: "POST", 
            headers: { "Content-Type": "application/json" }, 
            credentials: "include", 
            body: JSON.stringify({ 
                raids: result.raids
                // NOTE: We rely on the backend to find the 'wow-classic-anniversary' ID
                // to ensure we use real data from the DB, not a hardcoded frontend string.
            }) 
        });
        
        if (!res.ok) throw new Error("Failed to publish schedule");
        
        showToast("Schedule Published!", "success");
        if (window.refreshPlannerRaids) {
          window.refreshPlannerRaids();
        }
              setResult(null);
              
          } catch(e) { 
              showToast("Error publishing", "error"); 
          } finally { 
              setIsPublishing(false); 
          }
        };

  const DAYS = ["Wed", "Thu", "Fri", "Sat", "Sun", "Mon", "Tue"];
  const raidsByDay = useMemo(() => {
    const map: Record<string, any[]> = {};
    result?.raids?.forEach((r: any) => { if(!map[r.day]) map[r.day] = []; map[r.day].push(r); });
    return map;
  }, [result]);

  return (
    <div className="p-8 bg-[var(--c-surface)] min-h-screen text-[var(--c-ink)] relative">
      <div className="flex flex-col md:flex-row gap-6 mb-10 items-start md:items-end bg-[var(--c-sunken)] p-6 rounded-2xl border border-[var(--c-line)]">
        <div className="flex-1">
          <h1 className="text-2xl font-black uppercase tracking-tight">Lockout Scheduler</h1>
          <p className="text-xs text-[var(--c-ink-3)] mt-1">
            Current Lockout Week: <span className="text-cyan-700 dark:text-cyan-400 font-bold">{lockoutRange.start} — {lockoutRange.end}</span>
          </p>
        </div>
        <div className="flex gap-4 items-end">
            <div>
                <label className="text-[10px] font-bold uppercase text-[var(--c-ink-3)] block mb-2">Pick Date</label>
                <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} className="bg-[var(--c-sunken-2)] border border-[var(--c-line-strong)] rounded px-3 py-2 text-sm outline-none focus:border-cyan-500" />
            </div>
            <button onClick={handleGenerate} disabled={isSolving} className="bg-cyan-600 px-6 py-2 rounded font-bold hover:bg-cyan-500 disabled:opacity-50 transition-all shadow-lg shadow-cyan-900/20">
                {isSolving ? "Analyzing Roster..." : "Optimize Week"}
            </button>
            {result?.raids?.length > 0 && (
                <button onClick={handlePublish} disabled={isPublishing} className="bg-emerald-600 px-6 py-2 rounded font-bold hover:bg-emerald-500 disabled:opacity-50 transition-all">
                    Commit {result.raids.length} Raids
                </button>
            )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
        {DAYS.map(day => (
            <div key={day} className={`rounded-xl border p-4 flex flex-col min-h-[400px] ${raidsByDay[day] ? 'bg-[var(--c-bg-veil)] border-[var(--c-line)]' : 'bg-[var(--c-surface)] border-[var(--c-line-soft)] opacity-40'}`}>
                <div className="text-xs font-black uppercase tracking-widest text-[var(--c-ink-3)] mb-4 pb-2 border-b border-[var(--c-line-soft)] flex justify-between">
                    {day} <span>{raidsByDay[day]?.length || 0}</span>
                </div>
                <div className="space-y-3">
                    {raidsByDay[day]?.map((raid, idx) => (
                        <div 
                          key={idx} 
                          onClick={() => setSelectedRaid(raid)}
                          className="bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg p-3 hover:border-cyan-500/50 transition-colors cursor-pointer"
                        >
                            <div className="text-[10px] font-bold text-cyan-700 dark:text-cyan-400 uppercase truncate">{raid.instance}</div>
                            <div className="text-[10px] text-[var(--c-ink-3)] mb-2">{raid.time_range}</div>
                            <div className="flex flex-wrap gap-1">
                                {raid.members.slice(0, 5).map((m: any) => (
                                    <div key={m.id} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: CLASS_COLORS[m.class] || '#555' }} title={m.name} />
                                ))}
                                {raid.members.length > 5 && <span className="text-[8px] text-[var(--c-ink-4)]">+{raid.members.length - 5}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        ))}
      </div>

      {/* NEW: Raid Details Modal */}
      {selectedRaid && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-in fade-in" onClick={() => setSelectedRaid(null)} />
          <div className="relative w-full max-w-2xl bg-[var(--c-surface)] border border-[var(--c-line)] rounded-xl shadow-2xl flex flex-col max-h-[80vh] animate-in zoom-in-95">
            <div className="p-5 border-b border-[var(--c-line)] bg-[var(--c-bg-veil)] flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-[var(--c-ink)]">{selectedRaid.instance}</h3>
                <div className="text-xs text-[var(--c-ink-3)] uppercase tracking-widest">{selectedRaid.day} • {selectedRaid.time_range}</div>
              </div>
              <button onClick={() => setSelectedRaid(null)} className="text-[var(--c-ink-3)] hover:text-[var(--c-ink)] text-xl">✕</button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 grid grid-cols-2 gap-x-8 gap-y-2">
              {selectedRaid.members.map((m: any) => (
                <div key={m.id} className="flex items-center gap-3 p-2 rounded hover:bg-[var(--c-sunken)] border border-transparent hover:border-[var(--c-line-soft)] transition-colors">
                  <div className="w-1 h-8 rounded-full" style={{ backgroundColor: CLASS_COLORS[m.class] || '#555' }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-[var(--c-ink)] truncate">{m.name}</div>
                    <div className="text-[10px] text-[var(--c-ink-3)] uppercase tracking-wider">{m.role_type}</div>
                  </div>
                  {selectedRaid.raid_leader === m.id && <span className="text-[9px] font-bold bg-yellow-500/20 text-yellow-500 px-1.5 py-0.5 rounded border border-yellow-500/30">RL</span>}
                  {selectedRaid.loot_master === m.id && <span className="text-[9px] font-bold bg-cyan-500/20 text-cyan-500 px-1.5 py-0.5 rounded border border-cyan-500/30">ML</span>}
                </div>
              ))}
            </div>
            
            <div className="p-4 border-t border-[var(--c-line)] bg-[var(--c-bg-veil)] flex justify-between text-xs text-[var(--c-ink-4)]">
                <span>Total: {selectedRaid.size} Players</span>
                <span>ID: {selectedRaid.raid_id || 'New'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}